<script setup>
defineOptions({
  name: 'ToastContainer',
});

import { watch, ref } from 'vue';
import { usePage } from '@inertiajs/vue3';
import { useToast } from '@/composables/useToast.js';
import Toast from '@/components/Toast.vue';

const { toasts, removeToast, success, error, warning, info } = useToast();
const page = usePage();

// Track flash messages we've already shown using their timestamp.
// Keep only the last hour to avoid unbounded growth.
const FLASH_MEMORY_WINDOW_MS = 60 * 60 * 1000;
const shownFlashTimestamps = ref(new Map());

function pruneOldFlashTimestamps() {
  const cutoff = Date.now() - FLASH_MEMORY_WINDOW_MS;

  for (const [timestamp, shownAt] of shownFlashTimestamps.value) {
    if (shownAt < cutoff) {
      shownFlashTimestamps.value.delete(timestamp);
    }
  }
}

// Watch for Inertia flash messages and convert them to toasts
watch(
  () => page.props.flash,
  (flash) => {
    pruneOldFlashTimestamps();

    // Check if flash object exists in the current response
    const flashExists = flash !== undefined && flash !== null;

    if (!flashExists) {
      return;
    }

    // Process success message - flash.success is { message: string, timestamp: number }
    if (flash?.success?.message && flash?.success?.timestamp) {
      if (!shownFlashTimestamps.value.has(flash.success.timestamp)) {
        success(flash.success.message);
        shownFlashTimestamps.value.set(flash.success.timestamp, Date.now());
      }
    }

    // Process error message
    if (flash?.error?.message && flash?.error?.timestamp) {
      if (!shownFlashTimestamps.value.has(flash.error.timestamp)) {
        error(flash.error.message);
        shownFlashTimestamps.value.set(flash.error.timestamp, Date.now());
      }
    }

    // Process warning message
    if (flash?.warning?.message && flash?.warning?.timestamp) {
      if (!shownFlashTimestamps.value.has(flash.warning.timestamp)) {
        warning(flash.warning.message);
        shownFlashTimestamps.value.set(flash.warning.timestamp, Date.now());
      }
    }

    // Process info message
    if (flash?.info?.message && flash?.info?.timestamp) {
      if (!shownFlashTimestamps.value.has(flash.info.timestamp)) {
        info(flash.info.message);
        shownFlashTimestamps.value.set(flash.info.timestamp, Date.now());
      }
    }
  },
  { immediate: true, deep: true }
);

// Handle toast removal
function handleToastRemove(toastId) {
  removeToast(toastId);
}
</script>

<template>
  <!-- Toast Container - Fixed position, bottom-right -->
  <div
    aria-live="assertive"
    class="pointer-events-none fixed inset-0 z-50 flex items-end justify-end px-4 py-6 sm:items-end sm:justify-end sm:p-6"
  >
    <div class="flex w-full flex-col items-center space-y-4 sm:items-end">
      <!-- Toast notifications -->
      <transition-group
        name="toast"
        tag="div"
        class="flex flex-col space-y-4"
        enter-active-class="transform ease-out duration-300 transition"
        enter-from-class="translate-y-2 opacity-0 sm:translate-y-0 sm:translate-x-2"
        enter-to-class="translate-y-0 opacity-100 sm:translate-x-0"
        leave-active-class="transition ease-in duration-100"
        leave-from-class="opacity-100"
        leave-to-class="opacity-0"
      >
        <Toast
          v-for="toast in toasts"
          :key="toast.id"
          :id="toast.id"
          :type="toast.type"
          :title="toast.title"
          :message="toast.message"
          :duration="toast.duration"
          :persistent="toast.persistent"
          :link="toast.link"
          @remove="handleToastRemove"
        />
      </transition-group>
    </div>
  </div>
</template>

